import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaClock,
  FaRupeeSign,
  FaFileAlt,
  FaUserInjured,
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaUserMd,
  FaNotesMedical,
  FaStethoscope,
  FaCalendarCheck,
  FaRegCommentDots,
  FaPlus,
  FaPaperPlane,
  FaLock,
} from "react-icons/fa";
import { FiEye, FiExternalLink, FiPaperclip, FiX } from "react-icons/fi";

import RightWrapper from "../../../Components/CommonComponents/ComponentWrapper";
import { Button, Badge, StatCard, ConfirmDialog, Modal } from "../../../Components/UI";
import CustomSpinner from "../../../Components/CommonComponents/CustomSpinner";
import CameraCapture from "../../../Components/CommonComponents/CameraCapture";
import { END_POINT, getJwtToken } from "../../../Redux/AdminReducer/action";
import { useCheckRole, useGetLoggedInUser } from "../../../UtilityHookFunctions/LoggedInUser";
import { DownloadFile } from "../../../UtilityHookFunctions/OtherMongoHelperFunctions";
import TherapyRequestDetails, { StatusBadge, OriginBadge } from "./TherapyRequestDetails";

const errorText = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  "Something went wrong";

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const isImageFile = (filename = "") => {
  if (!filename) return false;
  const ext = filename.split(".").pop().toLowerCase();
  return ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
};

const fileUrl = (id) => (id ? `${END_POINT}/alternative_therapy/download/${id}` : null);

const DOCTOR_ROLES = ["MainDoctor", "Doctor", "AssistantDoctor", "admin"];

const roleLabel = (role) => {
  switch (role) {
    case "MainDoctor":
      return "Main Doctor";
    case "AssistantDoctor":
      return "Assistant Doctor";
    case "admin":
      return "Admin";
    default:
      return role || "Staff";
  }
};

const initialsOf = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase() || "?";

const sameDay = (a, b) => a.toDateString() === b.toDateString();

const dayLabel = (value) => {
  if (!value) return "Earlier records";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Earlier records";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(date, today)) return "Today";
  if (sameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const timeLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
};

const fetchItems = async (path) => {
  try {
    return await axios.get(`${END_POINT}/${path}`, {
      headers: { Authorization: getJwtToken() },
    });
  } catch (error) {
    toast.error(errorText(error));
  }
};

/**
 * Flattens the record into one ordered conversation. The doctor's prescribing
 * note opens the thread, then every posted message follows. Records saved
 * before the thread existed only kept the last comment and one file, so those
 * are folded in as undated entries rather than being dropped.
 */
const buildThread = (formData) => {
  if (!formData) return [];
  const thread = [];

  if (formData.note) {
    thread.push({
      key: "brief",
      text: formData.note,
      senderRole: "MainDoctor",
      senderName: "Main Doctor",
      label: "Prescribing requirement",
      createdAt: null,
    });
  }

  const messages = Array.isArray(formData.messages) ? formData.messages : [];

  if (messages.length) {
    messages.forEach((message, index) => {
      thread.push({
        key: message._id || `message-${index}`,
        text: message.text,
        file: message.file?.id ? message.file : null,
        senderRole: message.senderRole,
        senderId: message.senderId,
        senderName: message.senderName,
        system: message.system,
        createdAt: message.createdAt,
      });
    });
    return thread;
  }

  const consultantName = formData.consultant?.name || "Consultant";
  const legacyFile = formData.files?.[0];
  if (legacyFile?.id) {
    thread.push({
      key: "legacy-file",
      file: legacyFile,
      senderRole: "Consultant",
      senderName: consultantName,
      label: "Before this case had a thread",
      createdAt: null,
    });
  }
  if (formData.comment) {
    thread.push({
      key: "legacy-comment",
      text: formData.comment,
      senderRole: "Consultant",
      senderName: consultantName,
      label: "Before this case had a thread",
      createdAt: null,
    });
  }

  return thread;
};

const AttachmentCard = ({ file, tone = "light", onPreview }) => {
  const url = fileUrl(file?.id);
  const isImg = isImageFile(file?.name);

  if (isImg && url) {
    return (
      <div className="space-y-1.5">
        <button
          type="button"
          onClick={() => onPreview(file)}
          className="block overflow-hidden rounded-xl border border-black/10 bg-white/60"
        >
          <img
            src={url}
            alt={file.name}
            className="max-h-52 w-full object-cover transition duration-200 hover:scale-[1.02]"
          />
        </button>
        <div className="flex items-center justify-between gap-2">
          <span
            className={`truncate text-[11px] ${tone === "dark" ? "text-sky-100" : "text-slate-500"}`}
            title={file.name}
          >
            {file.name}
          </span>
          <button
            type="button"
            onClick={() => DownloadFile("alternative_therapy/download", file.id, file.name)}
            className={`shrink-0 text-[11px] font-semibold underline-offset-2 hover:underline ${
              tone === "dark" ? "text-white" : "text-sky-600"
            }`}
          >
            Download
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2.5 rounded-xl border p-2.5 ${
        tone === "dark" ? "border-white/25 bg-white/10" : "border-slate-200 bg-slate-50"
      }`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          tone === "dark" ? "bg-white/20 text-white" : "bg-sky-100 text-sky-700"
        }`}
      >
        <FiPaperclip className="h-4 w-4" />
      </div>
      <p
        className={`min-w-0 flex-1 truncate text-xs font-semibold ${
          tone === "dark" ? "text-white" : "text-slate-800"
        }`}
        title={file?.name}
      >
        {file?.name || "Attachment"}
      </p>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={() => onPreview(file)}
          className={`rounded-md p-1.5 transition ${
            tone === "dark" ? "text-sky-100 hover:bg-white/20" : "text-slate-500 hover:bg-slate-200"
          }`}
          title="Preview"
        >
          <FiEye className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => DownloadFile("alternative_therapy/download", file.id, file.name)}
          className={`rounded-md p-1.5 transition ${
            tone === "dark" ? "text-sky-100 hover:bg-white/20" : "text-slate-500 hover:bg-slate-200"
          }`}
          title="Download"
        >
          <FaDownload className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};

const UpdateAlternativeTherapy = () => {
  const { therapy_id } = useParams();
  const nav = useNavigate();
  const loggedInUser = useGetLoggedInUser();
  const checkRole = useCheckRole();

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [markDone, setMarkDone] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDoneModal, setShowConfirmDoneModal] = useState(false);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, file: null });

  const fileInputRef = useRef(null);
  const threadRef = useRef(null);

  // Role determinations
  const isDoctor = Boolean(
    checkRole("maindoctor") ||
      checkRole("admin") ||
      checkRole("doctor") ||
      loggedInUser?.role === "MainDoctor" ||
      loggedInUser?.role === "admin"
  );

  const isConsultant = Boolean(
    checkRole("consultant") || loggedInUser?.role === "Consultant"
  );

  const consultantId = formData?.consultant?._id || formData?.consultant;
  const isAssignedConsultant =
    isConsultant &&
    loggedInUser?._id &&
    String(consultantId) === String(loggedInUser._id);

  const loadData = () => {
    setLoading(true);
    fetchItems(`alternative_therapy/getForm/${therapy_id}`)
      .then((res) => {
        setFormData(res?.data || null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [therapy_id]);

  const isTreating = formData?.status === "treating";
  const isDone = formData?.status === "done";
  const isBid = formData?.status === "bid";

  // Mirrors the server rule: an unassigned consultant is a reader here.
  const canPost = isTreating && (!isConsultant || isAssignedConsultant);

  const thread = useMemo(() => buildThread(formData), [formData]);

  const attachments = useMemo(
    () => thread.filter((entry) => entry.file?.id).map((entry) => entry.file).reverse(),
    [thread]
  );

  // Newest message at the bottom, so the thread opens where the reading is.
  // Scrolling the pane itself rather than the anchor keeps the page still.
  useEffect(() => {
    const pane = threadRef.current;
    if (pane) pane.scrollTop = pane.scrollHeight;
  }, [thread.length]);

  const handleAppendTemplate = (templateText) => {
    setComment((prev) => {
      const current = prev ? prev.trim() + "\n\n" : "";
      return current + templateText;
    });
  };

  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();

    if (isDoctor && markDone && !showConfirmDoneModal) {
      setShowConfirmDoneModal(true);
      return;
    }

    setShowConfirmDoneModal(false);
    executeSubmission();
  };

  const executeSubmission = async () => {
    if (!selectedFile && !comment.trim() && !(isDoctor && markDone)) {
      toast.warning("Write a message, attach a file, or mark the therapy as completed.");
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(
      markDone ? "Marking therapy completed..." : "Posting to the case thread..."
    );

    try {
      const iformdata = new FormData();
      if (selectedFile) {
        iformdata.append("file", selectedFile);
      }
      if (comment.trim()) {
        iformdata.append("comment", comment.trim());
      }
      if (isDoctor && markDone) {
        iformdata.append("status", "done");
      }

      await axios.post(
        `${END_POINT}/alternative_therapy/update_therapy/${therapy_id}`,
        iformdata,
        {
          headers: { Authorization: getJwtToken() },
        }
      );

      toast.dismiss(toastId);
      toast.success(
        markDone ? "Therapy successfully marked as completed!" : "Message posted",
        { autoClose: 2000 }
      );

      setComment("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setMarkDone(false);

      loadData();
    } catch (error) {
      console.error("Update therapy error:", error);
      toast.dismiss(toastId);
      toast.error(errorText(error), { autoClose: 3500 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openFilePreview = (file) => {
    if (!file?.id) return;
    setPreviewModal({ isOpen: true, file });
  };

  const closeFilePreview = () => setPreviewModal({ isOpen: false, file: null });

  if (loading) {
    return (
      <RightWrapper>
        <div className="w-full min-h-[60vh] flex flex-col items-center justify-center p-8">
          <CustomSpinner />
          <p className="mt-4 text-sm text-slate-500">Loading therapy treatment details...</p>
        </div>
      </RightWrapper>
    );
  }

  const requestForm = formData?.requestForm || {};
  const patientName =
    requestForm?.name ||
    requestForm?.patientName ||
    requestForm?.fullName ||
    formData?.patientName ||
    "Patient Case";
  const patientAge = requestForm?.age || requestForm?.patientAge;
  const patientGender = requestForm?.gender || requestForm?.patientGender;
  const patientPhone = requestForm?.mobile || requestForm?.phone;

  const previewFile = previewModal.file;
  const previewIsImage = isImageFile(previewFile?.name);

  return (
    <RightWrapper>
      <div className="w-full space-y-6 p-2 md:p-6 max-w-7xl mx-auto">
        {/* Top Header Card */}
        <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => nav(-1)}
              icon={<FaArrowLeft className="h-3.5 w-3.5" />}
            >
              Back
            </Button>

            <div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                <Link to="/AlternativeTherapies" className="hover:text-sky-600 transition">
                  Alternative Therapies
                </Link>
                <span>/</span>
                <span className="text-slate-700 capitalize">
                  {formData?.therapy || "Therapy"}
                </span>
                <span>/</span>
                <span className="text-sky-600 font-semibold">Case Conversation</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900 capitalize">
                  {formData?.therapy ? `${formData.therapy} Treatment` : "Therapy Management"}
                </h1>
                <StatusBadge status={formData?.status} />
                {formData?.origin && <OriginBadge origin={formData.origin} />}
                {isDoctor && (
                  <Badge variant="primary" size="sm">
                    <FaUserMd className="mr-1 h-3 w-3" /> Doctor View
                  </Badge>
                )}
                {isAssignedConsultant && (
                  <Badge variant="success" size="sm">
                    <FaStethoscope className="mr-1 h-3 w-3" /> Assigned Consultant
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-center">
            {formData?._id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => nav(`/AlternativeTherapies/${formData._id}`)}
                icon={<FiExternalLink className="h-3.5 w-3.5" />}
              >
                View Request Overview
              </Button>
            )}
            {isBid && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => nav(`/Bids/${formData._id}`)}
              >
                Review Bids
              </Button>
            )}
          </div>
        </div>

        {/* Status Callout Banner */}
        {isDone ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/80 p-4 md:p-5 text-emerald-900 shadow-sm flex items-start gap-3.5">
            <FaCheckCircle className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm">
              <p className="font-bold text-emerald-950">Therapy Successfully Completed</p>
              <p className="text-emerald-800 mt-0.5">
                This alternative therapy program was marked completed on{" "}
                <strong>{formData?.complete_date || "Record"}</strong>. The full conversation and
                every shared document stay available below, read-only.
              </p>
            </div>
          </div>
        ) : isTreating ? (
          <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4 md:p-5 text-violet-900 shadow-sm flex items-start gap-3.5">
            <FaClock className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm">
              <p className="font-bold text-violet-950">Active Treatment in Progress</p>
              <p className="text-violet-800 mt-0.5">
                {isDoctor
                  ? "You and the assigned consultant share one thread on this case. Everything either of you posts — notes and files alike — stays visible to both sides."
                  : "You and the main doctor share one thread on this case. Post session notes and progress documents here; the doctor sees them straight away."}
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 text-amber-900 shadow-sm flex items-start gap-3">
            <FaExclamationTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm">
              <p className="font-bold">Therapy Not in Active Treatment Status</p>
              <p className="text-amber-800 mt-0.5">
                Current status is <strong>{formData?.status}</strong>. The conversation opens once a
                consultant bid is approved and treatment is officially commenced.
              </p>
            </div>
          </div>
        )}

        {/* Key Metrics Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Therapy Modality"
            value={<span className="capitalize">{formData?.therapy || "—"}</span>}
            icon={<FaNotesMedical className="h-5 w-5" />}
            color="primary"
          />

          <StatCard
            label="Treating Consultant"
            value={formData?.consultant?.name || "Unassigned"}
            icon={<FaUserMd className="h-5 w-5" />}
            color="info"
          />

          <StatCard
            label="Agreed Terms"
            value={
              formData?.bidPrice != null
                ? `₹${Number(formData.bidPrice).toLocaleString("en-IN")}`
                : "—"
            }
            icon={<FaRupeeSign className="h-5 w-5" />}
            color="success"
          />

          <StatCard
            label="Agreed Duration"
            value={formData?.bidDuration ? `${formData.bidDuration} Days` : "—"}
            icon={<FaCalendarCheck className="h-5 w-5" />}
            color="warning"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Conversation */}
          <div className="lg:col-span-8">
            <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                    <FaRegCommentDots className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800">Case Conversation</h2>
                    <p className="text-xs text-slate-500">
                      {formData?.consultant?.name || "Consultant"} &amp; the main doctor · notes and
                      files in one thread
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {attachments.length > 0 && (
                    <Badge variant="success" size="sm">
                      {attachments.length} {attachments.length === 1 ? "file" : "files"}
                    </Badge>
                  )}
                  {isDone && (
                    <Badge variant="default" size="sm">
                      <FaLock className="mr-1 h-2.5 w-2.5" /> Closed
                    </Badge>
                  )}
                </div>
              </div>

              {/* Message list */}
              <div
                ref={threadRef}
                className="max-h-[560px] min-h-[300px] space-y-4 overflow-y-auto bg-slate-50/70 p-4 sm:p-5"
              >
                {thread.length === 0 ? (
                  <div className="flex h-full min-h-[240px] flex-col items-center justify-center text-center">
                    <FaRegCommentDots className="h-9 w-9 text-slate-300" />
                    <p className="mt-2 text-sm font-semibold text-slate-600">
                      No messages on this case yet
                    </p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {canPost
                        ? "Start the conversation below — notes and files both land here."
                        : "The doctor and consultant have not posted anything yet."}
                    </p>
                  </div>
                ) : (
                  thread.map((entry, index) => {
                    const previous = thread[index - 1];
                    const showDay =
                      index === 0 || dayLabel(previous?.createdAt) !== dayLabel(entry.createdAt);

                    if (entry.system) {
                      return (
                        <React.Fragment key={entry.key}>
                          {showDay && (
                            <div className="flex items-center justify-center">
                              <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-slate-500 shadow-sm ring-1 ring-slate-200">
                                {dayLabel(entry.createdAt)}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center justify-center">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-800 ring-1 ring-emerald-200">
                              <FaCheckCircle className="h-3 w-3" />
                              {entry.senderName} {entry.text}
                              {timeLabel(entry.createdAt) && (
                                <span className="text-emerald-600">
                                  · {timeLabel(entry.createdAt)}
                                </span>
                              )}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    }

                    const isMine =
                      entry.senderId &&
                      loggedInUser?._id &&
                      String(entry.senderId) === String(loggedInUser._id);
                    const fromDoctor = DOCTOR_ROLES.includes(entry.senderRole);

                    return (
                      <React.Fragment key={entry.key}>
                        {showDay && (
                          <div className="flex items-center justify-center">
                            <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-slate-500 shadow-sm ring-1 ring-slate-200">
                              {dayLabel(entry.createdAt)}
                            </span>
                          </div>
                        )}

                        <div
                          className={`flex items-end gap-2.5 ${
                            isMine ? "flex-row-reverse" : "flex-row"
                          }`}
                        >
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                              fromDoctor
                                ? "bg-sky-100 text-sky-700"
                                : "bg-emerald-100 text-emerald-700"
                            }`}
                            title={entry.senderName}
                          >
                            {initialsOf(entry.senderName)}
                          </div>

                          <div
                            className={`min-w-0 max-w-[85%] sm:max-w-[75%] ${
                              isMine ? "items-end text-right" : "items-start"
                            }`}
                          >
                            <div
                              className={`mb-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500 ${
                                isMine ? "justify-end" : "justify-start"
                              }`}
                            >
                              <span className="font-semibold text-slate-700">
                                {isMine ? "You" : entry.senderName || "Staff"}
                              </span>
                              <span
                                className={`rounded-full px-1.5 py-px text-[10px] font-medium ${
                                  fromDoctor
                                    ? "bg-sky-50 text-sky-700"
                                    : "bg-emerald-50 text-emerald-700"
                                }`}
                              >
                                {roleLabel(entry.senderRole)}
                              </span>
                              {entry.label && <span className="italic">{entry.label}</span>}
                              {timeLabel(entry.createdAt) && (
                                <span>{timeLabel(entry.createdAt)}</span>
                              )}
                            </div>

                            <div
                              className={`space-y-2.5 rounded-2xl px-3.5 py-2.5 text-left shadow-sm ${
                                isMine
                                  ? "rounded-br-sm bg-sky-600 text-white"
                                  : "rounded-bl-sm border border-slate-200 bg-white text-slate-800"
                              }`}
                            >
                              {entry.text && (
                                <p className="whitespace-pre-wrap break-words text-xs leading-relaxed sm:text-sm">
                                  {entry.text}
                                </p>
                              )}
                              {entry.file?.id && (
                                <AttachmentCard
                                  file={entry.file}
                                  tone={isMine ? "dark" : "light"}
                                  onPreview={openFilePreview}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
              </div>

              {/* Composer */}
              {canPost ? (
                <form onSubmit={handleFormSubmit} className="border-t border-slate-200 bg-white p-4 sm:p-5">
                  <div className="mb-2.5 flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        handleAppendTemplate(
                          "• Session Milestone:\n- Completed session [X] of [Total]\n- Patient demonstrated positive symptom response"
                        )
                      }
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 transition hover:bg-slate-100 hover:text-sky-600"
                    >
                      <FaPlus className="h-2 w-2" /> Session Milestone
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleAppendTemplate(
                          "• Patient Tolerance:\n- Vitals and tolerance stable throughout\n- No adverse reactions observed"
                        )
                      }
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 transition hover:bg-slate-100 hover:text-sky-600"
                    >
                      <FaPlus className="h-2 w-2" /> Tolerance &amp; Vitals
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleAppendTemplate(
                          "• Next Steps & Prescription:\n- Continue dietary/herbal cadence for next 7 days\n- Follow-up check scheduled"
                        )
                      }
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 transition hover:bg-slate-100 hover:text-sky-600"
                    >
                      <FaPlus className="h-2 w-2" /> Next Steps
                    </button>
                  </div>

                  {selectedFile && (
                    <div className="mb-2.5 flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50/70 p-2.5">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <FaFileAlt className="h-4 w-4 shrink-0 text-emerald-600" />
                        <div className="min-w-0">
                          <p
                            className="truncate text-xs font-bold text-emerald-950"
                            title={selectedFile.name}
                          >
                            {selectedFile.name}
                          </p>
                          <p className="text-[11px] font-medium text-emerald-700">
                            {formatFileSize(selectedFile.size)} · attaches to this message
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        className="shrink-0 rounded-md p-1.5 text-rose-600 transition hover:bg-rose-50"
                        title="Remove attachment"
                      >
                        <FiX className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}

                  <div className="flex items-end gap-2">
                    <input
                      ref={fileInputRef}
                      id="therapy-file-input"
                      type="file"
                      name="file"
                      className="hidden"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="shrink-0 rounded-xl border border-slate-200 p-2.5 text-slate-500 transition hover:bg-slate-50 hover:text-sky-600"
                      title="Attach a report or image"
                    >
                      <FiPaperclip className="h-4 w-4" />
                    </button>
                    <CameraCapture
                      label=""
                      className="shrink-0 !rounded-xl !border-slate-200 !p-2.5 !text-slate-500 hover:!text-sky-600"
                      onCapture={(file) => {
                        if (fileInputRef.current) fileInputRef.current.value = "";
                        setSelectedFile(file);
                      }}
                    />

                    <textarea
                      id="comment-textarea"
                      name="livecomment"
                      rows={2}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      onKeyDown={(e) => {
                        // Enter sends; Shift+Enter keeps the newline, since these
                        // notes are often bulleted lists.
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleFormSubmit();
                        }
                      }}
                      placeholder={
                        isDoctor
                          ? "Reply to the consultant, add supervisory notes or guidance..."
                          : "Share session progress, patient response or instructions..."
                      }
                      className="w-full resize-none rounded-xl border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-800 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 sm:text-sm"
                    />

                    <Button
                      type="submit"
                      variant={markDone ? "success" : "primary"}
                      size="md"
                      isLoading={isSubmitting}
                      className="shrink-0"
                      icon={<FaPaperPlane className="h-3.5 w-3.5" />}
                    >
                      Send
                    </Button>
                  </div>

                  <p className="mt-2 text-[11px] text-slate-400">
                    Enter to send · Shift + Enter for a new line
                  </p>

                  {isDoctor && (
                    <label
                      className={`mt-3 flex cursor-pointer select-none items-start gap-3.5 rounded-xl border-2 p-4 transition-all ${
                        markDone
                          ? "border-emerald-400 bg-emerald-50 shadow-sm shadow-emerald-600/10"
                          : "border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={markDone}
                        onChange={(e) => setMarkDone(e.target.checked)}
                        className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-900">
                          <FaCheckCircle
                            className={`h-4 w-4 ${markDone ? "text-emerald-600" : "text-slate-400"}`}
                          />
                          Close the therapy with this message
                          {markDone && (
                            <Badge variant="success" size="sm">
                              Will finalize
                            </Badge>
                          )}
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500">
                          Sends your message and marks the course <strong>done</strong> with today's
                          date. The thread stays readable for both sides but no one can post again.
                        </p>
                      </div>
                    </label>
                  )}
                </form>
              ) : (
                <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 text-center text-xs text-slate-500">
                  {isDone
                    ? `This therapy was completed on ${formData?.complete_date || "record"}. The thread is read-only.`
                    : isConsultant && !isAssignedConsultant
                    ? "This therapy is not assigned to you, so you can read the thread but not post."
                    : "The conversation opens once a bid is approved and treatment begins."}
                </div>
              )}
            </div>
          </div>

          {/* Case context sidebar */}
          <div className="space-y-6 lg:col-span-4 lg:sticky lg:top-6">
            {/* Patient & Case Brief Card */}
            <div className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                    <FaUserInjured className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800">Patient &amp; Case Brief</h2>
                    <p className="text-xs text-slate-500">Who this conversation is about</p>
                  </div>
                </div>
                {patientAge && (
                  <Badge variant="default" size="sm">
                    {patientAge} yrs {patientGender ? `· ${patientGender}` : ""}
                  </Badge>
                )}
              </div>

              <div className="space-y-3 text-xs">
                <div className="space-y-1 rounded-xl border border-slate-100 bg-slate-50/60 p-3.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    Patient Name
                  </span>
                  <p className="text-sm font-bold text-slate-800">{patientName}</p>
                  {patientPhone && (
                    <p className="font-mono text-xs text-slate-500">{patientPhone}</p>
                  )}
                </div>

                <div className="space-y-1 rounded-xl border border-slate-100 bg-slate-50/60 p-3.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    Assigned Consultant
                  </span>
                  <p className="text-sm font-bold text-slate-800">
                    {formData?.consultant?.name || "Pending Assignment"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formData?.consultant?.email || "No email on record"}
                  </p>
                </div>
              </div>
            </div>

            {/* Every file shared in the thread, newest first */}
            <div className="space-y-3 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <FaFileAlt className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800">Shared Documents</h2>
                    <p className="text-xs text-slate-500">Everything attached to this case</p>
                  </div>
                </div>
              </div>

              {attachments.length > 0 ? (
                <div className="max-h-72 space-y-2 overflow-y-auto pr-0.5">
                  {attachments.map((file, index) => (
                    <AttachmentCard
                      key={`${file.id}-${index}`}
                      file={file}
                      onPreview={openFilePreview}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 p-5 text-center text-xs text-slate-400">
                  <FaFileAlt className="mx-auto mb-1.5 h-6 w-6 text-slate-300" />
                  <p className="font-semibold text-slate-600">No documents shared yet</p>
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    Attach reports to a message and they show up here.
                  </p>
                </div>
              )}
            </div>

            {isDone && (
              <div className="space-y-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                    <FaCheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-emerald-950">Therapy Course Closed</h3>
                    <p className="text-xs text-emerald-700">
                      Completed on {formData?.complete_date || "—"}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => nav("/AlternativeTherapies")}
                >
                  Return to Alternative Therapies List
                </Button>
              </div>
            )}

            {/* Detailed Request Questionnaire / Parameters */}
            {Object.keys(requestForm).length > 0 && (
              <TherapyRequestDetails
                requestForm={requestForm}
                title="Full Intake Questionnaire & Parameters"
              />
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Marking Complete */}
      <ConfirmDialog
        isOpen={showConfirmDoneModal}
        onClose={() => setShowConfirmDoneModal(false)}
        title="Confirm Therapy Completion"
        message="Are you sure you want to mark this therapy as completed? This posts your message, closes the case and makes the conversation read-only for both sides."
        confirmText="Yes, Mark Completed"
        confirmVariant="primary"
        onConfirm={executeSubmission}
        isLoading={isSubmitting}
      />

      {/* Lightbox / Preview Modal for Uploaded Files */}
      <Modal
        isOpen={previewModal.isOpen}
        onClose={closeFilePreview}
        title={previewFile?.name || "File Preview"}
        maxWidth="max-w-3xl"
        footer={
          <div className="flex w-full items-center justify-between">
            <span className="max-w-xs truncate text-xs text-slate-500">{previewFile?.name}</span>
            <div className="flex items-center gap-2">
              {previewFile?.id && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    DownloadFile("alternative_therapy/download", previewFile.id, previewFile.name)
                  }
                  icon={<FaDownload className="h-3 w-3" />}
                >
                  Download File
                </Button>
              )}
              <Button size="sm" variant="primary" onClick={closeFilePreview}>
                Close
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex min-h-[300px] flex-col items-center justify-center p-2">
          {previewIsImage ? (
            <img
              src={fileUrl(previewFile?.id)}
              alt={previewFile?.name}
              className="max-h-[65vh] w-auto rounded-xl border border-slate-200 object-contain shadow-sm"
            />
          ) : (
            <iframe
              src={fileUrl(previewFile?.id)}
              title={previewFile?.name}
              className="h-[65vh] w-full rounded-xl border border-slate-200"
            />
          )}
        </div>
      </Modal>
    </RightWrapper>
  );
};

export default UpdateAlternativeTherapy;
